import { Icon, type IconName } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { INCOMING_INVOICE_STATE_COLOR } from "@msuite/utils/incoming-invoices";

/** The states the list filters on; a merged duplicate is never listed, so it is not one of them. */
export const INCOMING_INVOICE_STATE_FILTERS = [
	"all",
	"received",
	"approved",
	"paid",
	"rejected",
	"duplicate",
] as const;

export type IncomingInvoiceStateFilter = (typeof INCOMING_INVOICE_STATE_FILTERS)[number];

const STATE_ICONS: Record<Exclude<IncomingInvoiceStateFilter, "all">, IconName> = {
	received: "Inbox",
	approved: "Check",
	paid: "Landmark",
	rejected: "XCircle",
	duplicate: "Copy",
};

/** Props Interface */
interface IncomingInvoiceStatePickerProps {
	value: IncomingInvoiceStateFilter;
	onChange: (value: IncomingInvoiceStateFilter) => void;
}

export function IncomingInvoiceStatePicker({ value, onChange }: IncomingInvoiceStatePickerProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const items = INCOMING_INVOICE_STATE_FILTERS.map((state) =>
		state === "all"
			? {
					label: t("general.all"),
					value: state,
					cyId: `incoming-invoice-state-picker-${state}`,
					leftIcon: <Icon name="GalleryVerticalEnd" />,
				}
			: {
					label: t(`incoming_invoices.state.${state}`),
					value: state,
					cyId: `incoming-invoice-state-picker-${state}`,
					leftIcon: (
						<Icon
							name={STATE_ICONS[state]}
							stroke={`var(--color-${INCOMING_INVOICE_STATE_COLOR[state]}-500)`}
						/>
					),
				},
	);

	/** Render */
	return (
		<SegmentedControl
			value={value}
			items={items}
			onChange={(state) => onChange(state as IncomingInvoiceStateFilter)}
		/>
	);
}
